'use client';

import React from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@unity/cloud-ui';
import { useTheme } from '@unity/cloud-ui/styles';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';

export const ListGroup = (props: z.input<typeof listGroupSchema>) => {
  const { title, collapsible = false, children } = props;
  const [expanded, setExpanded] = React.useState(true);
  const { vars, spacing } = useTheme();

  const expandIcon = collapsible ? <CaretDownIcon /> : '';

  function handleAccordionClick() {
    if (collapsible) {
      setExpanded((prev) => !prev);
    }
  }

  return (
    <Accordion
      defaultExpanded={true}
      expanded={expanded}
      disableGutters
      sx={{
        border: `1px solid ${vars.palette.divider}`,
        borderRadius: '4px',
        '& > .MuiAccordion-heading .MuiAccordionSummary-expandIconWrapper': {
          transform: 'rotate(-180deg)',
        },
      }}
      onChange={handleAccordionClick}
    >
      {collapsible ? (
        <AccordionSummary
          expandIcon={expandIcon}
          sx={{
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px',
            '.MuiAccordionSummary-content.Mui-expanded': {
              margin: spacing(1.5, 0),
            },
            '&.Mui-expanded': {
              minHeight: '48px !important',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
            borderBottomLeftRadius: '3px',
            borderBottomRightRadius: '3px',
            '&:hover': {
              backgroundColor: vars.palette.primary.lighter,
            },
          }}
        >
          <Typography variant="h5" component="div">
            {title}
          </Typography>
        </AccordionSummary>
      ) : (
        <Box component="div" py={1.5} px={2} borderBottom={`1px solid ${vars.palette.divider}`} fontWeight="bold">
          {title}
        </Box>
      )}

      <AccordionDetails
        sx={{
          border: 'none',
          p: 0,
        }}
      >
        <Box
          component="ul"
          sx={{
            listStyleType: 'none',
            marginBlockStart: 0,
            marginBlockEnd: 0,
            paddingInlineStart: 0,
            '& > li': {
              padding: 2,
              margin: 0,
              '&:nth-of-type(odd)': {
                backgroundColor: (theme) => theme.vars.palette.action.hover,
              },
              '&:nth-of-type(even)': {
                backgroundColor: (theme) => theme.vars.palette.common.inverseWhite,
              },
            },
          }}
        >
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const documentationURL = getComponentDocumentationUrl('list-group');

const descriptions = {
  listGroup: {
    schema: `Creates a list group. \n\n\n\n**Example**\n\`\`\`mdx\n<ListGroup title="Title">\n  <ListItem>\n    The content of the item\n  </ListItem>\n</ListGroup>\n\`\`\`\n\n${documentationURL}`,
    title: 'The title of the list group.',
    collapsible: 'Whether the list group is collapsible or not.',
    children: 'The content of the list group. Can be anything.',
  },
};

export const listGroupSchema = z
  .object({
    title: z.string().describe(descriptions.listGroup.title).optional(),
    collapsible: z.boolean().describe(descriptions.listGroup.collapsible).optional(),
    children: z.any().describe(descriptions.listGroup.children),
  })
  .describe(descriptions.listGroup.schema);
